import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useScrollToTop } from '../hooks/useScrollToTop';
import NewsletterSignup from '../components/NewsletterSignup';
import { HeartIcon, BoltIcon, CakeIcon, MoonIcon, SparklesIcon } from '@heroicons/react/24/outline';

const CategoriesPage: React.FC = () => {
  // Scroll to top when page loads
  useScrollToTop();

  const getCategoryIcon = (categoryId: string) => {
    const icons = {
      'stress-relief': <HeartIcon className="w-6 h-6" />,
      mindfulness: <SparklesIcon className="w-6 h-6" />,
      sleep: <MoonIcon className="w-6 h-6" />,
      'self-care': <CakeIcon className="w-6 h-6" />,
      wellness: <BoltIcon className="w-6 h-6" />,
    };
    return icons[categoryId as keyof typeof icons] || icons.wellness;
  };

  const categories = [
    { id: 'stress-relief', name: 'Stress Relief', description: 'Practical techniques to reduce stress and anxiety', href: '/category/stress-relief' },
    { id: 'mindfulness', name: 'Mindfulness', description: 'Meditation and mindfulness practices', href: '/category/mindfulness' },
    { id: 'sleep', name: 'Sleep Better', description: 'Tips for better sleep and relaxation', href: '/category/sleep' },
    { id: 'self-care', name: 'Self Care', description: 'Self-care practices for mental wellness', href: '/category/self-care' },
    { id: 'wellness', name: 'Wellness', description: 'General wellness and lifestyle tips', href: '/category/wellness' }
  ];

  return (
    <>
      <Helmet>
        <title>Wellness Categories - Destress Daily</title>
        <meta name="description" content="Explore our comprehensive collection of wellness categories including stress relief, mindfulness, sleep, self-care, and wellness tips." />
        <meta name="keywords" content="wellness categories, stress relief tips, mindfulness advice, sleep optimization, self care, mental wellness" />
      </Helmet>

      <div className="pt-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-health-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-gradient rounded-2xl mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Explore Wellness Categories
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Discover expert-curated wellness tips organized by category. Find the perfect advice 
                for your stress relief journey, from mindfulness to self-care and sleep.
              </p>
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category) => {
              // Use consistent blue for all categories with lighter borders
              const bgColorClass = 'bg-primary-50';
              const textColorClass = 'text-primary-600';
              const borderColorClass = 'border-primary-100';
              const hoverBgClass = 'hover:bg-primary-100';

              return (
                <Link
                  key={category.id}
                  to={category.href}
                  className={`group block bg-white rounded-2xl shadow-sm border ${borderColorClass} p-8 transition-all duration-300 ${hoverBgClass} hover:shadow-lg hover:scale-105`}
                >
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 ${bgColorClass} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <div className={textColorClass}>
                        {getCategoryIcon(category.id)}
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                      {category.name}
                    </h3>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {category.description}
                    </p>
                    
                    <div className={`inline-flex items-center text-sm font-semibold ${textColorClass} group-hover:translate-x-1 transition-transform duration-300`}>
                      Explore Category
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Featured Content Section */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose Our Wellness Categories?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Each category is carefully curated by wellness professionals to provide you with 
                the most effective and practical stress relief advice.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-xl mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Verified</h3>
                <p className="text-gray-600">All tips are reviewed and verified by health professionals</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-xl mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Actionable Advice</h3>
                <p className="text-gray-600">Practical tips you can implement immediately</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-xl mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Comprehensive Coverage</h3>
                <p className="text-gray-600">From stress relief to mindfulness, we cover all aspects of mental wellness</p>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <NewsletterSignup variant="hero" />
      </div>
    </>
  );
};

export default CategoriesPage; 